import { useRouter } from "@tanstack/react-router";
import omit from "lodash/omit";
import { useAddJointAccount } from "creso-core/hooks";
import { Client } from "creso-core/types";
import {
  Button,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  V2Dialog,
} from "web-design-system";
import { handleError } from "@/utils/error";

export function ConfirmJointAccountDialog({
  clientId,
  onConfirm,
}: {
  clientId: number;
  onConfirm: (jointClient: Client) => void;
}) {
  const router = useRouter();
  const { mutateAsync: addJointAccount, isPending } = useAddJointAccount();

  const close = () => {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      router.navigate({
        to: ".",
        search: (prev) => omit(prev, ["modal"]),
        replace: true,
      });
    }
  };

  const handleConfirm = async () => {
    try {
      const jointClient = await addJointAccount(clientId);
      onConfirm(jointClient);
    } catch (error) {
      handleError(error, "Failed to set up joint account");
    }
  };

  return (
    <V2Dialog
      defaultOpen
      onOpenChange={(open) => {
        if (!open && !isPending) close();
      }}
    >
      <DialogContent className="p-6 gap-6 space-y-0 w-128 max-w-none overflow-visible">
        <DialogHeader className="gap-5">
          <DialogTitle className="!text-lg">
            Confirm Joint Account Setup?
          </DialogTitle>
          <DialogDescription className="!leading-6">
            Adding a second holder will create a separate joint account with
            this client as the primary holder.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isPending}>
            Yes, confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </V2Dialog>
  );
}
